"use client";

import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { FaInfoCircle, FaBug, FaTools, FaCircle, FaTachometerAlt, FaDatabase, FaChartLine, FaFileExport, FaSearch, FaBook, FaUsers, FaGlobe } from "react-icons/fa";
import packageJson from "@/package.json";
import changelogJson from "@/changelogs.json";
import { useTranslations, useLocale } from "next-intl";

import Header from "@/components/header";
import Footer from "@/components/footer";
import Changelog, { ChangelogInfo } from "@/components/changelog";

import AuthorSearch from "./[mtid]/AuthorSearch";

// Styles
import "react-toastify/dist/ReactToastify.css";
import "@/app/loadingv2.css";

import { loadImpactFactorData, loadNorwayData } from "./[mtid]/mtmtHelpers";

const preloadData = async () => {
  try {
    await loadImpactFactorData();
    await loadNorwayData();
  } catch (error) {
    toast.error(`${(error as Error).message}`, {
      position: "bottom-right",
      autoClose: 5000,
    });
  }
};

export default function MTMTStatistics() {
  const t = useTranslations("core");
  const changelogT = useTranslations("changelog");
  const headerT = useTranslations("header");
  const homeT = useTranslations("home");

  const [changelogs, setChangelogs] = useState<[string, ChangelogInfo][]>([]);

  useEffect(() => {
    const allChangelogs = Object.entries(changelogJson) as [
      string,
      ChangelogInfo
    ][];
    // Sort by version number (newest first)
    const sortedChangelogs = allChangelogs.sort((a, b) => {
      const versionA = a[0].split('.').map(Number);
      const versionB = b[0].split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if (versionA[i] !== versionB[i]) {
          return versionB[i] - versionA[i];
        }
      }
      return 0;
    });
    // Show only last 4 changelogs
    setChangelogs(sortedChangelogs.slice(0, 4));

    void preloadData();
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 smooth-scroll">
      <Header name={t("title")} desc={headerT("description")} />
      <Changelog />

      {/* Hero */}
      <section className="relative bg-linear-to-br from-gray-900 to-black text-white">
        <div className="container mx-auto max-w-7xl px-4 py-12 sm:py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                {t("title")} <span className="text-bme-main">v{packageJson.version}</span>
              </h1>
              <p className="mt-4 text-white/80 text-base sm:text-lg">
                {headerT("description")}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="/docs" className="cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition">
                  <FaBook className="h-4 w-4" />
                  {homeT("docs")}
                </a>
              </div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6">
              <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
                <div className="p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition">
                  <div className="flex justify-center mb-2">
                    <FaDatabase className="h-5 w-5 sm:h-6 sm:w-6 text-bme-main" />
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-bme-main">2M+</div>
                  <div className="text-[10px] sm:text-xs text-white/70 mt-1">{homeT("analytics.analyzed")}</div>
                </div>
                <div className="p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition">
                  <div className="flex justify-center mb-2">
                    <FaChartLine className="h-5 w-5 sm:h-6 sm:w-6 text-bme-main" />
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-bme-main">CIT</div>
                  <div className="text-[10px] sm:text-xs text-white/70 mt-1">{homeT("analytics.citations")}</div>
                </div>
                <div className="p-3 sm:p-4 rounded-lg bg-white/5 hover:bg-white/10 transition">
                  <div className="flex justify-center mb-2">
                    <FaInfoCircle className="h-5 w-5 sm:h-6 sm:w-6 text-bme-main" />
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-bme-main">IF</div>
                  <div className="text-[10px] sm:text-xs text-white/70 mt-1">{homeT("analytics.impactFactor")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-linear-to-r from-transparent via-bme-main to-transparent opacity-80" />
      </section>

      <main className="grow">
        {/* Search Section */}
        <section id="search" className="container mx-auto max-w-7xl px-4 py-10">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {homeT("authorSearchTitle")}
            </h2>
            <AuthorSearch />
          </div>
        </section>
        

        {/* Analytics Highlights */}
        <section className="container mx-auto max-w-7xl px-4 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="rounded-lg bg-bme-main/10 p-3">
                  <FaDatabase className="h-5 w-5 text-bme-main" />
                </div>
                <p className="text-3xl font-bold text-gray-800">API</p>
              </div>
              <p className="text-sm text-gray-600">{homeT("analytics.realtime")}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="rounded-lg bg-bme-main/10 p-3">
                  <FaChartLine className="h-5 w-5 text-bme-main" />
                </div>
                <p className="text-3xl font-bold text-gray-800"><span className="text-bme-main">2x</span></p>
              </div>
              <p className="text-sm text-gray-600">{homeT("analytics.speed")}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="rounded-lg bg-bme-main/10 p-3">
                  <FaDatabase className="h-5 w-5 text-bme-main" />
                </div>
                <p className="text-3xl font-bold text-gray-800">2M+</p>
              </div>
              <p className="text-sm text-gray-600">{homeT("analytics.analyzed")}</p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-gray-50 border-y border-gray-200">
          <div className="container mx-auto max-w-7xl px-4 py-12">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">{homeT("how.title")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
                <div className="rounded-lg bg-bme-main/10 p-4 w-fit mb-4">
                  <FaSearch className="h-6 w-6 text-bme-main" />
                </div>
                <h3 className="font-medium text-gray-800 mb-2">{homeT("how.search")}</h3>
                <p className="text-sm text-gray-600">{homeT("how.searchDesc")}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
                <div className="rounded-lg bg-bme-main/10 p-4 w-fit mb-4">
                  <FaChartLine className="h-6 w-6 text-bme-main" />
                </div>
                <h3 className="font-medium text-gray-800 mb-2">{homeT("how.analyze")}</h3>
                <p className="text-sm text-gray-600">{homeT("how.analyzeDesc")}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
                <div className="rounded-lg bg-bme-main/10 p-4 w-fit mb-4">
                  <FaFileExport className="h-6 w-6 text-bme-main" />
                </div>
                <h3 className="font-medium text-gray-800 mb-2">{homeT("how.export")}</h3>
                <p className="text-sm text-gray-600">{homeT("how.exportDesc")}</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container mx-auto max-w-5xl px-4 py-12">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">{homeT("faq.title")}</h2>
          <div className="divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white">
            <details className="group p-4">
              <summary className="cursor-pointer flex items-center justify-between text-gray-800 font-medium">
                {homeT("faq.q1")}
                <span className="ml-4 text-gray-500 group-open:rotate-180 transition">^</span>
              </summary>
              <p className="mt-2 text-sm text-gray-600">{homeT("faq.a1")}</p>
            </details>
            <details className="group p-4">
              <summary className="cursor-pointer flex items-center justify-between text-gray-800 font-medium">
                {homeT("faq.q2")}
                <span className="ml-4 text-gray-500 group-open:rotate-180 transition">^</span>
              </summary>
              <p className="mt-2 text-sm text-gray-600">{homeT("faq.a2")}</p>
            </details>
            <details className="group p-4">
              <summary className="cursor-pointer flex items-center justify-between text-gray-800 font-medium">
                {homeT("faq.q3")}
                <span className="ml-4 text-gray-500 group-open:rotate-180 transition">^</span>
              </summary>
              <p className="mt-2 text-sm text-gray-600">{homeT("faq.a3")}</p>
            </details>
            <details className="group p-4">
              <summary className="cursor-pointer flex items-center justify-between text-gray-800 font-medium">
                {homeT("faq.q4")}
                <span className="ml-4 text-gray-500 group-open:rotate-180 transition">^</span>
              </summary>
              <p className="mt-2 text-sm text-gray-600">{homeT("faq.a4")}</p>
            </details>
            <details className="group p-4">
              <summary className="cursor-pointer flex items-center justify-between text-gray-800 font-medium">
                {homeT("faq.q5")}
                <span className="ml-4 text-gray-500 group-open:rotate-180 transition">^</span>
              </summary>
              <p className="mt-2 text-sm text-gray-600">{homeT("faq.a5")}</p>
            </details>
            <details className="group p-4">
              <summary className="cursor-pointer flex items-center justify-between text-gray-800 font-medium">
                {homeT("faq.q6")}
                <span className="ml-4 text-gray-500 group-open:rotate-180 transition">^</span>
              </summary>
              <p className="mt-2 text-sm text-gray-600">{homeT("faq.a6")}</p>
            </details>
            <details className="group p-4">
              <summary className="cursor-pointer flex items-center justify-between text-gray-800 font-medium">
                {homeT("faq.q7")}
                <span className="ml-4 text-gray-500 group-open:rotate-180 transition">^</span>
              </summary>
              <p className="mt-2 text-sm text-gray-600">{homeT("faq.a7")}</p>
            </details>
          </div>
        </section>

        {/* Features */}
        <section className="bg-gray-50 border-t border-gray-200">
          <div className="container mx-auto max-w-7xl px-4 py-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
                <div className="rounded-lg bg-bme-main/10 p-3 w-fit mb-3">
                  <FaInfoCircle className="h-5 w-5 text-bme-main" />
                </div>
                <h3 className="font-medium text-gray-800 mb-2">{homeT("features.title")}</h3>
                <p className="text-sm text-gray-600">{homeT("features.featuresDesc")}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
                <div className="rounded-lg bg-bme-main/10 p-3 w-fit mb-3">
                  <FaTools className="h-5 w-5 text-bme-main" />
                </div>
                <h3 className="font-medium text-gray-800 mb-2">{homeT("features.tools")}</h3>
                <p className="text-sm text-gray-600">{homeT("features.toolsDesc")}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition-shadow">
                <div className="rounded-lg bg-bme-main/10 p-3 w-fit mb-3">
                  <FaBug className="h-5 w-5 text-bme-main" />
                </div>
                <h3 className="font-medium text-gray-800 mb-2">{homeT("features.quality")}</h3>
                <p className="text-sm text-gray-600">{homeT("features.qualityDesc")}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="container mx-auto max-w-7xl px-4 py-12">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">{homeT("stats.title")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center hover:shadow-lg transition-shadow">
              <div className="flex justify-center mb-3">
                <div className="rounded-lg bg-bme-main/10 p-3">
                  <FaUsers className="h-6 w-6 text-bme-main" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">1000+</p>
              <p className="text-sm text-gray-600 mt-1">{homeT("stats.activeUsers")}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center hover:shadow-lg transition-shadow">
              <div className="flex justify-center mb-3">
                <div className="rounded-lg bg-bme-main/10 p-3">
                  <FaGlobe className="h-6 w-6 text-bme-main" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">2</p>
              <p className="text-sm text-gray-600 mt-1">{homeT("stats.languages")}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center hover:shadow-lg transition-shadow">
              <div className="flex justify-center mb-3">
                <div className="rounded-lg bg-bme-main/10 p-3">
                  <FaFileExport className="h-6 w-6 text-bme-main" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">3</p>
              <p className="text-sm text-gray-600 mt-1">{homeT("stats.exportFormats")}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center hover:shadow-lg transition-shadow">
              <div className="flex justify-center mb-3">
                <div className="rounded-lg bg-bme-main/10 p-3">
                  <FaDatabase className="h-6 w-6 text-bme-main" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">24/7</p>
              <p className="text-sm text-gray-600 mt-1">{homeT("stats.availability")}</p>
            </div>
          </div>
        </section>

        {/* Changelog */}
        <section className="container mx-auto max-w-7xl px-4 py-12">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            {changelogT("newUpdates")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {changelogs.map(([version, changelog]) => (
              <div key={version} className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="text-base font-medium text-gray-700 mb-3">
                  v{version} - {changelog.title}
                </h3>

                {changelog.updates.some((item) => item.type === 'feature') && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <FaInfoCircle />
                      <h4 className="font-medium">{changelogT('features')}</h4>
                    </div>
                    <ul className="space-y-1 ml-6">
                      {changelog.updates
                        .filter((item) => item.type === 'feature')
                        .map((item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <FaCircle className="text-gray-400 text-[6px] mt-1.5 shrink-0" />
                            <span className="text-gray-600 text-sm">{item.description}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                {changelog.updates.some((item) => item.type === 'bugfix') && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <FaBug />
                      <h4 className="font-medium">{changelogT('bugfixes')}</h4>
                    </div>
                    <ul className="space-y-1 ml-6">
                      {changelog.updates
                        .filter((item) => item.type === 'bugfix')
                        .map((item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <FaCircle className="text-gray-400 text-[6px] mt-1.5 shrink-0" />
                            <span className="text-gray-600 text-sm">{item.description}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                {changelog.updates.some((item) => item.type === 'improvement') && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <FaTools />
                      <h4 className="font-medium">{changelogT('improvements')}</h4>
                    </div>
                    <ul className="space-y-1 ml-6">
                      {changelog.updates
                        .filter((item) => item.type === 'improvement')
                        .map((item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <FaCircle className="text-gray-400 text-[6px] mt-1.5 shrink-0" />
                            <span className="text-gray-600 text-sm">{item.description}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                {changelog.updates.some((item) => item.type === 'performance') && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <FaTachometerAlt />
                      <h4 className="font-medium">{changelogT('performance')}</h4>
                    </div>
                    <ul className="space-y-1 ml-6">
                      {changelog.updates
                        .filter((item) => item.type === 'performance')
                        .map((item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <FaCircle className="text-gray-400 text-[6px] mt-1.5 shrink-0" />
                            <span className="text-gray-600 text-sm">{item.description}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>

      <ToastContainer />
      <Footer />
    </div>
  );
}
